import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { firebaseService } from './firebase.service';
import { debugInfo } from '../utils/debug';
import { 
  ApiResponse, 
  Ticket, 
  OCRData, 
  Appeal, 
  SessionData,
  DisputeReasonData,
  Payment,
  TicketFields 
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.api.interceptors.request.use(
      async (config) => {
        const token = await firebaseService.getIdToken();
        const appCheckToken = await firebaseService.getAppCheckToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (appCheckToken) {
          config.headers['X-Firebase-AppCheck'] = appCheckToken;
        }
        if (process.env.NODE_ENV !== 'production' && config.url?.includes('/billing/me')) {
          const headerValue = String(config.headers?.Authorization || '');
          const rawToken = headerValue.startsWith('Bearer ') ? headerValue.slice(7) : headerValue;
          const parts = rawToken ? rawToken.split('.') : [];
          debugInfo('[billing/me] auth header', {
            hasAuthHeader: !!headerValue,
            tokenParts: parts.length,
          });
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        const status = error.response?.status;
        const requestUrl = String(error.config?.url || '');
        const responseError = String(error.response?.data?.error || '');
        const authHeader = String(error.config?.headers?.Authorization || '');
        const shouldForceLogout =
          status === 401 &&
          authHeader.startsWith('Bearer ') &&
          requestUrl !== '/auth/session' &&
          ['Unauthorized', 'Token expired', 'Invalid token', 'Authentication failed'].includes(responseError);

        if (shouldForceLogout) {
          void firebaseService.signOut();
          window.location.href = '/login';
        }
        
        const message = error.response?.data?.message || 
                       error.response?.data?.error || 
                       error.message || 
                       'An unexpected error occurred';
        
        throw new Error(message);
      }
    );
  }

  async uploadTicket(file: File, ticketData: {
    type: string;
    description?: string;
    location?: string;
    dateIssued?: string;
  }): Promise<ApiResponse<Ticket>> {
    try {
      const formData = new FormData();
      formData.append('ticketFile', file);
      formData.append('type', ticketData.type);
      
      if (ticketData.description) {
        formData.append('description', ticketData.description);
      }
      if (ticketData.location) {
        formData.append('location', ticketData.location);
      }
      if (ticketData.dateIssued) {
        formData.append('dateIssued', ticketData.dateIssued);
      }

      const response = await this.api.post<ApiResponse<Ticket>>('/tickets/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async startTicketExtraction(file: File): Promise<ApiResponse<{ ticketId: string; appealSessionId?: string; fields: TicketFields; ticket?: Ticket }>> {
    try {
      const formData = new FormData();
      formData.append('ticketFile', file);

      const response = await this.api.post<ApiResponse<{ ticketId: string; appealSessionId?: string; fields: TicketFields }>>(
        '/tickets/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateTicketFields(
    ticketId: string,
    fields: TicketFields
  ): Promise<ApiResponse<{ ticketId: string; fields: TicketFields; ticket?: Ticket }>> {
    try {
      const response = await this.api.put<ApiResponse<{ ticketId: string; fields: TicketFields; ticket?: Ticket }>>(
        `/tickets/${ticketId}/fields`,
        { fields },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async submitEvidence(
    ticketId: string,
    payload: {
      contextDetails: Record<string, string | boolean>;
      selectedReasons: string[];
      extraReasonText?: string;
    },
    files: { file: File; type: string }[]
  ): Promise<ApiResponse<{ ticketId: string }>> {
    try {
      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));
      files.forEach((u, idx) => {
        formData.append('files', u.file, `${u.type}-${idx}-${u.file.name}`);
        formData.append(`fileTypes[${idx}]`, u.type);
      });

      const response = await this.api.post<ApiResponse<{ ticketId: string }>>(
        `/tickets/${ticketId}/evidence`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTicket(ticketId: string): Promise<ApiResponse<{ ticketId: string; fields: TicketFields; ticket?: Ticket }>> {
    try {
      const response = await this.api.get<ApiResponse<{ ticketId: string; fields: TicketFields; ticket?: Ticket }>>(`/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getOCRResults(ticketId: string): Promise<ApiResponse<OCRData>> {
    try {
      const response = await this.api.get<ApiResponse<OCRData>>(`/tickets/${ticketId}/ocr`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async generateAppeal(
    ticketId: string, 
    disputeData: DisputeReasonData
  ): Promise<ApiResponse<Appeal>> {
    try {
      const response = await this.api.post<ApiResponse<Appeal>>('/tickets/generate-appeal', {
        ticketId,
        additionalInfo: this.formatDisputeData(disputeData),
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTicketHistory(userId: string): Promise<ApiResponse<{ tickets: Ticket[]; total: number }>> {
    try {
      const response = await this.api.get<ApiResponse<{ tickets: Ticket[]; total: number }>>(
        `/users/${userId}/tickets`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateTicketStatus(
    userId: string, 
    ticketId: string, 
    status: string, 
    notes?: string
  ): Promise<ApiResponse<Ticket>> {
    try {
      const response = await this.api.put<ApiResponse<Ticket>>(
        `/users/${userId}/tickets/${ticketId}`,
        { status, notes }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getUserSession(userId: string): Promise<ApiResponse<{ sessionData: SessionData }>> {
    try {
      const response = await this.api.get<ApiResponse<{ sessionData: SessionData }>>(
        `/users/${userId}/session`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateUserSession(
    userId: string, 
    sessionData: SessionData
  ): Promise<ApiResponse<{ sessionData: SessionData }>> {
    try {
      const response = await this.api.put<ApiResponse<{ sessionData: SessionData }>>(
        `/users/${userId}/session`,
        { sessionData }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createPayment(paymentData: {
    amount: number;
    currency?: string;
    ticketId?: string;
    description?: string;
  }): Promise<ApiResponse<{
    paymentId: string;
    clientSecret: string;
    paymentIntentId: string;
    amount: number;
    currency: string;
  }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>('/payments/create-intent', paymentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getPaymentStatus(paymentId: string): Promise<ApiResponse<Payment>> {
    try {
      const response = await this.api.get<ApiResponse<Payment>>(`/payments/${paymentId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async downloadAppealPDF(appealId: string): Promise<Blob> {
    try {
      const response = await this.api.get(`/appeals/${appealId}/pdf`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAppeal(appealId: string, content: string): Promise<ApiResponse<Appeal>> {
    try {
      const response = await this.api.put<ApiResponse<Appeal>>(`/appeals/${appealId}`, {
        content,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getSubmissionGuidance(ticketType: string): Promise<ApiResponse<{
    cityGuidance: any;
    privateGuidance: any;
  }>> {
    try {
      const response = await this.api.get<ApiResponse<any>>(`/guidance/${ticketType}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async uploadEvidence(ticketId: string, files: File[]): Promise<ApiResponse<string[]>> {
    try {
      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append(`evidence_${index}`, file);
      });

      const response = await this.api.post<ApiResponse<string[]>>(
        `/tickets/${ticketId}/evidence`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async chatBootstrap(sessionId: string, payload?: { language?: string }): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/chat/bootstrap`,
        payload || {}
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async chatMessages(sessionId: string, limit: number = 200): Promise<{ messages: any[] }> {
    try {
      const response = await this.api.get<{ messages: any[] }>(
        `/sessions/${sessionId}/chat/messages?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async chatNext(
    sessionId: string,
    payload: { userMessage?: string; quickReplyValue?: string }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/chat/next`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async chatRollback(
    sessionId: string,
    payload: { targetStepKey: string; needsReviewSlots?: string[]; consumeRollback?: boolean }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/chat/rollback`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async refreshRecommendation(
    sessionId: string,
    payload: { stepKey: string; stepType?: string }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/recommendations/refresh`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async submitRecommendationFeedback(
    sessionId: string,
    recId: string,
    payload: {
      userDecision: 'CONTINUE' | 'ADD_DETAILS' | 'KEEP_ANYWAY' | 'GO_BACK';
      decisionPayload?: Record<string, any>;
      stepKey?: string;
    }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/recommendations/${recId}/feedback`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async aiReviewDecision(
    sessionId: string,
    payload: { stepKey: string; decision: 'CONTINUE' | 'DISMISSED' | 'ADD_DETAILS_OPENED' }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/ai-review/decision`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async aiReviewAddDetails(
    sessionId: string,
    payload: { stepKey: string; text: string }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/ai-review/add-details`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateMitigationContext(
    sessionId: string,
    payload: { reasons: string[]; notes: Record<string, string> }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/mitigation`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAppealPath(
    sessionId: string,
    payload: { appealPath: 'AT_FAULT' | 'NOT_AT_FAULT' }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/appeal-path`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async refreshWeatherContext(
    sessionId: string
  ): Promise<ApiResponse<{ weather: any; reason?: string }>> {
    try {
      const response = await this.api.post<ApiResponse<{ weather: any; reason?: string }>>(
        `/sessions/${sessionId}/context/weather/refresh`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async initSessionEvidence(
    sessionId: string,
    payload: { category: 'PAYMENT' | 'SIGNAGE'; contentType: string; sizeBytes: number }
  ): Promise<ApiResponse<{ evidenceId: string; storagePath: string; uploadUrl: string }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/evidence/init`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async completeSessionEvidence(
    sessionId: string,
    evidenceId: string,
    file: File,
    payload?: { language?: string }
  ): Promise<ApiResponse<{ evidenceId: string; status: string; extracted?: any; summary?: string }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (payload?.language) {
        formData.append('language', payload.language);
      }

      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/evidence/${evidenceId}/complete`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async confirmSessionEvidence(
    sessionId: string,
    evidenceId: string,
    payload: { confirmed: boolean; correction?: string }
  ): Promise<ApiResponse<{ evidenceId: string; confirmed: boolean }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/evidence/${evidenceId}/confirm`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAppealSession(sessionId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.get<ApiResponse<any>>(`/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async generateSessionAppeal(
    sessionId: string,
    payload?: { language?: string; currentLetter?: string; termsAccepted?: boolean }
  ): Promise<ApiResponse<{ text: string; count: number; limit: number }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/generate`,
        payload || {}
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateSessionAppealLetter(
    sessionId: string,
    payload: { text: string }
  ): Promise<ApiResponse<{ text: string }>> {
    try {
      const response = await this.api.put<ApiResponse<any>>(
        `/sessions/${sessionId}/appeal-letter`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateSessionExtraNote(
    sessionId: string,
    payload: { note: string }
  ): Promise<ApiResponse<{ note: string }>> {
    try {
      const response = await this.api.put<ApiResponse<any>>(
        `/sessions/${sessionId}/extra-note`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async acceptSessionTerms(
    sessionId: string,
    payload: { version: string }
  ): Promise<ApiResponse<{ termsAcceptedAt: number; termsAcceptedVersion: string }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/accept-terms`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async acceptHumanReviewDisclaimer(
    sessionId: string,
    payload: { version: string }
  ): Promise<ApiResponse<{ humanReviewDisclaimerAcceptedAt: number; humanReviewDisclaimerVersion: string }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/sessions/${sessionId}/accept-human-review-disclaimer`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createAuthSession(
    payload?: { sessionId?: string },
    authToken?: string | null
  ): Promise<ApiResponse<{ uid: string; email?: string | null }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        '/auth/session',
        payload || {},
        authToken
          ? {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          : undefined
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createCheckoutSession(
    payload: { returnUrl: string; plan?: string }
  ): Promise<ApiResponse<{ url: string }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        '/billing/create-checkout-session',
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getBillingStatus(
    appealSessionId: string
  ): Promise<ApiResponse<{ active: boolean; status?: string | null }>> {
    try {
      const response = await this.api.get<ApiResponse<any>>(
        `/billing/status?appealSessionId=${encodeURIComponent(appealSessionId)}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getBillingMe(
    cacheBust?: boolean
  ): Promise<ApiResponse<{
    active: boolean;
    status?: string | null;
    currentPeriodEnd?: any;
    capUsd?: number;
    costUsdTotal?: number;
    tokenUsageTotal?: number;
    isOverCap?: boolean;
    estimatedTokensCap?: number | null;
    planType?: string;
    subscriptionStatus?: string;
    humanReview?: { remaining: number; totalGranted: number; resetAt?: any | null };
    letterCredits?: { remaining: number; totalGranted: number };
    entitlements?: { canGenerateLetters: boolean; canUseHumanReview: boolean };
  }>> {
    try {
      const token = (await firebaseService.getIdToken())?.trim();
      if (!token) {
        throw new Error('Authentication required to check billing status.');
      }
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid authentication token format.');
      }
      const url = cacheBust ? `/billing/me?ts=${Date.now()}` : '/billing/me';
      const response = await this.api.get<ApiResponse<any>>(url, {
        headers: { 'Cache-Control': 'no-store', Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getUsageMe(): Promise<ApiResponse<{
    limitCents: number;
    remainingCents: number;
    monthTotalCents: number;
    totalsByType: Record<string, number>;
    recentEvents: Array<{
      id: string;
      type: string;
      costCentsEstimated: number;
      createdAt?: any;
    }>;
  }>> {
    try {
      const response = await this.api.get<ApiResponse<any>>('/usage/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async useHumanReview(sessionId?: string): Promise<ApiResponse<{ remaining: number }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>('/billing/human-review/use', {
        sessionId: sessionId || null,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async requestHumanReview(
    ticketId: string,
    payload: { appealLetter: string }
  ): Promise<ApiResponse<{ requestId: string; remaining: number }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/tickets/${ticketId}/human-review-requests`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async requestExperiencedReview(payload: {
    caseId: string;
    userEmail: string;
    letterVersionId?: string | null;
    letterText?: string | null;
  }): Promise<ApiResponse<{ ok: boolean; requestId: string; status: string }>> {
    try {
      const response = await this.api.post<ApiResponse<any>>('/review-requests', payload, {
        headers: { 'Idempotency-Key': `${payload.caseId}-${payload.userEmail}` },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getReviewRequest(caseId: string): Promise<ApiResponse<{
    request: any | null;
    reviewState?: {
      canSubmit: boolean;
      disableReason: string;
      remaining: number;
      totalGranted: number;
      resetAt?: any | null;
      addonEligible?: boolean;
      submittedRecently?: boolean;
      requestStatus?: string | null;
      requestId?: string | null;
    };
  }>> {
    try {
      const response = await this.api.get<ApiResponse<any>>(
        `/review-requests?caseId=${encodeURIComponent(caseId)}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAdminCases(): Promise<ApiResponse<{ cases: any[] }>> {
    try {
      const response = await this.api.get<ApiResponse<{ cases: any[] }>>('/admin/cases');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAdminCaseDetail(caseId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.api.get<ApiResponse<any>>(`/admin/cases/${encodeURIComponent(caseId)}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateAdminCaseStatus(
    caseId: string,
    payload: { status: 'in_review' | 'completed' }
  ): Promise<ApiResponse<{ ok: boolean; caseId: string; status: string }>> {
    try {
      const response = await this.api.patch<ApiResponse<any>>(
        `/admin/cases/${encodeURIComponent(caseId)}/status`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTicketStats(userId: string): Promise<ApiResponse<{
    total: number;
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  }>> {
    try {
      const response = await this.api.get<ApiResponse<any>>(`/users/${userId}/stats`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async searchTickets(
    userId: string,
    query?: string,
    filters?: any,
    page: number = 1,
    limit: number = 20
  ): Promise<ApiResponse<{
    tickets: Ticket[];
    total: number;
    page: number;
    totalPages: number;
  }>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (query) params.append('q', query);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.type) params.append('type', filters.type);

      const response = await this.api.get<ApiResponse<any>>(
        `/users/${userId}/tickets/search?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  private formatDisputeData(disputeData: DisputeReasonData): string {
    let formatted = '';

    if (disputeData.emergencySituation) {
      formatted += `Emergency Situation: ${disputeData.emergencySituation}\n\n`;
    }

    if (disputeData.signVisibility) {
      const signLabels = {
        no_sign: 'No sign was visible',
        blocked: 'Sign was blocked or obstructed',
        misleading: 'Sign was misleading or confusing',
        clear: 'Sign was clear and visible'
      };
      formatted += `Sign Visibility: ${signLabels[disputeData.signVisibility]}\n\n`;
    }

    if (disputeData.weatherConditions) {
      formatted += `Weather Conditions: ${disputeData.weatherConditions}\n\n`;
    }

    if (disputeData.commonReasons && disputeData.commonReasons.length > 0) {
      formatted += `Common Dispute Reasons:\n${disputeData.commonReasons.map(reason => `- ${reason}`).join('\n')}\n\n`;
    }

    if (disputeData.additionalComments) {
      formatted += `Additional Comments: ${disputeData.additionalComments}`;
    }

    return formatted.trim();
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.api.get('/health', {
        timeout: 5000,
      });
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }
}

export const apiService = new ApiService();
export default apiService;
